export function useSettingsGuard() {
  const {
    userCanManageSubscription,
    isCurrentSubscriptionActive,
    validatePaymentMethodUpdateCheckoutSession,
    createPaymentMethodUpdateCheckoutSession,
    clearCheckoutSessions,
  } = useSubscriptionApi();

  const { makeGuardResult } = useRouteGuard();
  const { resolveSettingsRouteContext, createSettingsPath } =
    useSettingsRoute();

  /**
   * Guard the route when inside the settings flow
   * @return GuardResult
   */
  const guardFeatureRoute: FeatureGuard = async (route) => {
    const routeContext = resolveSettingsRouteContext(route);

    if (!routeContext.isRecognized) {
      devConsole.warn(
        `🚦 guard rule triggered: unrecognized route name for ${route.path}`,
        routeContext,
      );
      return makeGuardResult(route, "root");
    }

    if (
      routeContext.matches.settingsFlow === "strict" &&
      routeContext.query[SESSION_ID_QUERY_PARAM]
    ) {
      const sessionId = routeContext.query[SESSION_ID_QUERY_PARAM] as string;
      devConsole.info(
        "🚦 guard rule triggered: evaluating session id query",
        sessionId,
      );

      const checkoutStatus =
        await validatePaymentMethodUpdateCheckoutSession(sessionId);
      if (checkoutStatus) {
        return makeGuardResult(route, {
          redirect: "root",
        });
      }

      return makeGuardResult(route, {
        redirect: {
          path: createSettingsPath({
            section: "payment-method",
          }),
        },
      });
    }

    if (routeContext.matches.paymentFlow) {
      const session = await createPaymentMethodUpdateCheckoutSession();
      if (!session) {
        devConsole.error(
          "🚦 guard rule triggered: failed to create checkout session for payment method update",
        );
        return makeGuardResult(route, {
          redirect: createSettingsPath(),
        });
      }
    }

    return makeGuardResult(route, "continue");
  };

  /**
   * Guard the route when leaving the subscription flow
   * @param from
   * @param to
   * @return GuardResult
   */
  const guardExitRoute: ExitGuard = ({ from, to }) => {
    const fromRouteContext = resolveSettingsRouteContext(from);

    if (!fromRouteContext.isRecognized) return makeGuardResult(to, "continue");

    const toRouteContext = resolveSettingsRouteContext(to);

    const isLeavingPaymentStep =
      fromRouteContext.matches.paymentFlow &&
      !toRouteContext.matches.paymentFlow;

    if (isLeavingPaymentStep) {
      devConsole.info(
        "🚦 guard rule triggered: leaving payment step, clearing checkout sessions",
      );
      clearCheckoutSessions();
    }

    return makeGuardResult(to, "continue");
  };

  /**
   * @return GuardResult
   */
  const guardRouteAccess: AccessGuard = ({ to }) => {
    const routeContext = resolveSettingsRouteContext(to);

    if (!routeContext.isRecognized) return makeGuardResult(to, "abort");

    if (!userCanManageSubscription.value && routeContext.matches.invitesFlow) {
      devConsole.warn(
        "🚦 guard rule triggered: user is trying to access invites that they don't have permissions for",
      );

      return makeGuardResult(to, {
        redirect: createSettingsPath(),
      });
    }

    if (!routeContext.matches.settingsFlow) {
      return makeGuardResult(to, "continue");
    }

    if (
      !userCanManageSubscription.value &&
      routeContext.matches.settingsFlow !== "strict"
    ) {
      devConsole.warn(
        "🚦 guard rule triggered: user is trying to access settings that they don't have permissions for",
      );

      return makeGuardResult(to, {
        redirect: createSettingsPath(),
      });
    }

    if (
      !isCurrentSubscriptionActive.value &&
      routeContext.matches.paymentFlow
    ) {
      devConsole.warn(
        "🚦 guard rule triggered: cannot update the payment method on an inactive subscription",
      );

      return makeGuardResult(to, {
        redirect: createSettingsPath(),
      });
    }

    return makeGuardResult(to, "continue");
  };

  return {
    name: "settingsGuard",
    guardFeatureRoute,
    guardExitRoute,
    guardRouteAccess,
  };
}
